import React, { useEffect, useRef } from "react";
import { composeTemplateImage } from "@/utility";

interface OverlayConfig {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  clipAsCircle?: boolean;
}

type TextAlign = "left" | "center" | "right";

interface TextConfig {
  x?: number;
  y?: number;
  maxWidth?: number;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  textAlign?: TextAlign;
  lineHeight?: number;
}

export interface ImageTemplateComposerProps {
  templateSrc: string;              // Template image URL
  overlayImageSrc?: string | null;  // User image URL
  text?: string | null;             // Text to render

  overlayConfig?: OverlayConfig;
  textConfig?: TextConfig;

  canvasWidth?: number;
  canvasHeight?: number;

  onExport?: (dataUrl: string) => void; // Called after drawing
}

/**
 * Reusable component to draw:
 * - template image
 * - overlay (user) image
 * - text
 * on a canvas, with configurable positions & sizes.
 */
export const ImageTemplateComposer: React.FC<ImageTemplateComposerProps> = ({
  templateSrc,
  overlayImageSrc,
  text,
  overlayConfig = {},
  textConfig = {},
  canvasWidth,
  canvasHeight,
  onExport,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!templateSrc) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    composeTemplateImage({
      templateSrc,
      overlayImageSrc: overlayImageSrc || undefined,
      text: text || undefined,
      overlayConfig,
      textConfig,
      canvasWidth,
      canvasHeight,
      canvas,
    })
      .then(({ dataUrl }) => {
        if (cancelled) return;
        if (onExport) {
          onExport(dataUrl);
        }
      })
      .catch((err) => {
        console.warn("Failed to compose image", err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    templateSrc,
    overlayImageSrc,
    text,
    overlayConfig,
    textConfig,
    canvasWidth,
    canvasHeight,
    onExport,
  ]);

  return (
    <canvas
      ref={canvasRef}
      style={{ maxWidth: "100%", height: "auto" }}
    />
  );
};
